import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getAdminId, getSupportSocket } from "../api/services/supportSocket";
import {
  getAdminAlerts,
  getUnreadAdminAlertCount,
  getHeaderInboxAlerts,
  getHeaderInboxUnreadCount,
  markAllAdminAlertsRead,
  markAdminAlertRead,
  markAdminAlertUnread,
  pushAdminAlert,
  subscribeAdminAlerts,
  setAdminAlertsOwner,
  clearHeaderInbox,
} from "../utils/adminAlertsStore";
import {
  getPrefs,
  setPrefs,
  isAlertAllowed,
  isChatAlreadyOpen,
} from "../utils/adminNotifyPrefs";
import { ensureAssignedUnreadSocketListeners } from "../utils/assignedChatUnreadStore";

function canUseBrowserNotification() {
  return typeof window !== "undefined" && "Notification" in window;
}

export async function ensureBrowserNotificationPermission() {
  if (!canUseBrowserNotification()) return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

function showBrowserNotification(alert, navigate) {
  if (!canUseBrowserNotification() || Notification.permission !== "granted") return;
  try {
    const n = new Notification(alert.title || "Naseebi Admin", {
      body: alert.message || "",
      tag: String(alert.id || `naseebi-${Date.now()}`),
      icon: "/favicon.ico",
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch (_) {}
      if (navigate && alert.route) navigate(alert.route);
      n.close();
    };
  } catch (e) {
    console.warn("Browser notification failed:", e?.message || e);
  }
}

function showInAppToast(alert, navigate) {
  toast(
    (t) => (
      <button
        type="button"
        onClick={() => {
          toast.dismiss(t.id);
          if (alert.route && navigate) navigate(alert.route);
        }}
        className="text-left w-full"
      >
        <span className="block text-[11px] font-bold uppercase tracking-wide text-[#FF2D38]">
          Naseebi Admin
        </span>
        <span className="block text-sm font-semibold text-gray-900 mt-0.5">{alert.title}</span>
        {alert.message ? (
          <span className="block text-xs text-gray-600 mt-1 leading-snug">{alert.message}</span>
        ) : null}
        <span className="block text-[10px] text-gray-400 mt-2">Click to open →</span>
      </button>
    ),
    {
      duration: 5500,
      position: "top-right",
      style: {
        borderRadius: "12px",
        border: "1px solid #fecdd3",
        padding: "12px 14px",
        maxWidth: "360px",
      },
    }
  );
}

function normalizeTicketAlert(ticket) {
  if (!ticket?.id) return null;
  const who = ticket.displayName || ticket.guest_name || "Someone";
  const guest = String(ticket.session_type || "").toUpperCase() === "GUEST";
  return {
    id: `support_ticket-${ticket.id}-${ticket.created_at || ticket.last_message_at || Date.now()}`,
    type: "support_ticket",
    title: guest ? "New guest support request" : "New support ticket",
    message: `${who} needs help — claim it from Available tickets.`,
    route: `/support?ticketId=${ticket.id}&view=available`,
    refId: ticket.id,
    at: new Date().toISOString(),
    meta: { ticketId: ticket.id, displayName: who },
  };
}

/**
 * Global admin alert listener (socket + browser + toast + inbox).
 */
export function useAdminAlerts({ navigate, enabled = true } = {}) {
  const adminId = getAdminId();
  const [alerts, setAlerts] = useState(() => {
    if (adminId != null) setAdminAlertsOwner(adminId);
    return getAdminAlerts(adminId);
  });
  const [unread, setUnread] = useState(() => getUnreadAdminAlertCount(adminId));
  const [headerAlerts, setHeaderAlerts] = useState(() => getHeaderInboxAlerts(adminId));
  const [headerUnread, setHeaderUnread] = useState(() => getHeaderInboxUnreadCount(adminId));
  const [prefs, setPrefsState] = useState(() => getPrefs(adminId));
  const [browserPermission, setBrowserPermission] = useState(() =>
    canUseBrowserNotification() ? Notification.permission : "denied"
  );

  useEffect(() => {
    setPrefsState(getPrefs(adminId));
    setAdminAlertsOwner(adminId);
    setAlerts(getAdminAlerts(adminId));
    setUnread(getUnreadAdminAlertCount(adminId));
    setHeaderAlerts(getHeaderInboxAlerts(adminId));
    setHeaderUnread(getHeaderInboxUnreadCount(adminId));
  }, [adminId]);

  useEffect(() => subscribeAdminAlerts((next) => {
    setAlerts(next);
    setUnread(next.filter((a) => !a.read).length);
    const header = getHeaderInboxAlerts(adminId);
    setHeaderAlerts(header);
    setHeaderUnread(header.filter((a) => !a.read).length);
  }), [adminId]);

  useEffect(() => {
    if (!enabled) return undefined;

    ensureAssignedUnreadSocketListeners();

    if (prefs.enabled !== false && prefs.browser) {
      ensureBrowserNotificationPermission().then((p) => setBrowserPermission(p));
    } else {
      setBrowserPermission(canUseBrowserNotification() ? Notification.permission : "denied");
    }

    const socket = getSupportSocket();
    const ingest = (payload) => {
      if (!payload?.id || !payload?.title) return;
      const currentPrefs = getPrefs(adminId);
      if (!isAlertAllowed(currentPrefs, payload.type)) return;

      // Deduplicate support tickets: adminAlert + supportTicketNew may both fire.
      const existing = getAdminAlerts(adminId);
      if (
        payload.type === "support_ticket" &&
        payload.refId &&
        existing.some(
          (a) =>
            a.type === "support_ticket" &&
            String(a.refId) === String(payload.refId) &&
            Date.now() - new Date(a.receivedAt || a.at || 0).getTime() < 15000
        )
      ) {
        return;
      }

      pushAdminAlert(payload, adminId);

      const skipToast = isChatAlreadyOpen(payload);
      if (currentPrefs.toast && !skipToast) {
        showInAppToast(payload, navigate);
      }
      if (currentPrefs.browser && !skipToast) {
        showBrowserNotification(payload, navigate);
      }
    };

    const onAlert = (payload) => ingest(payload);
    const onTicket = (ticket) => {
      const alert = normalizeTicketAlert(ticket);
      if (alert) ingest(alert);
    };

    socket.on("adminAlert", onAlert);
    socket.on("supportTicketNew", onTicket);

    return () => {
      socket.off("adminAlert", onAlert);
      socket.off("supportTicketNew", onTicket);
    };
  }, [enabled, navigate, adminId, prefs.browser, prefs.enabled]);

  const markRead = useCallback((id) => markAdminAlertRead(id, adminId), [adminId]);
  const markUnread = useCallback((id) => markAdminAlertUnread(id, adminId), [adminId]);
  const markAllRead = useCallback(() => markAllAdminAlertsRead(adminId), [adminId]);
  const clearHeaderList = useCallback(() => clearHeaderInbox(adminId), [adminId]);
  const requestBrowserPermission = useCallback(async () => {
    const p = await ensureBrowserNotificationPermission();
    setBrowserPermission(p);
    return p;
  }, []);

  const updatePrefs = useCallback(
    (patch) => {
      const next = setPrefs(adminId, patch);
      setPrefsState(next);
      return next;
    },
    [adminId]
  );

  return {
    alerts,
    unread,
    headerAlerts,
    headerUnread,
    prefs,
    updatePrefs,
    browserPermission,
    markRead,
    markUnread,
    markAllRead,
    clearHeaderList,
    requestBrowserPermission,
  };
}
